<template>
  <PmModalPure title="Suche" @close="$emit('close')">
    <div class="PmSearch">
      <PmTabNavigationPure
        v-model:selected-tab-id="selectedSearchOption"
        :tabs="searchOptions"
      />

      <PmSearchPure
        v-model:value="searchterm"
        :results="normalizedSearchResults"
        :now="now"
        :is-loading="isLoading"
        @select="select"
      />
    </div>
  </PmModalPure>
</template>

<script lang="ts">
import { defineComponent } from 'vue'

const COMPONENT_NAME = 'PmSearch'

export const propTypes = {} as const

export default defineComponent({
  name: COMPONENT_NAME,
})
</script>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { addYears, subYears, differenceInCalendarDays } from 'date-fns'
import { orderBy } from 'lodash-es'
import { useQuery } from '@vue/apollo-composable'

import { statusLookupJob } from '@/utilities/persoplan'
import { formatWithLocale, parseServerDateString } from '@/utilities/date'
import { getPurchaseOrderStatus } from '@/composition/usePurchaseOrderStatus'

import PmTabNavigationPure from '@/components/basics/PmTabNavigation/PmTabNavigationPure.vue'
import PmModalPure from '@/components/basics/PmModalPure.vue'
import PmSearchPure, {
  type Props as PropsSearchPure,
  type Selection as SearchResultSelection,
} from '@/components/persoplan/Search/PmSearchPure.vue'

import type { CalendarJumpTarget } from '@/composition/useCalendarJump'

import { SearchDocument } from '../../../../generated/graphql'

export interface Props {
  now?: Date
}

export type NormalizedResult = PropsSearchPure['results'][number] & {
  projectId?: number
}

export type Emit = {
  select: CalendarJumpTarget
}

const props = withDefaults(defineProps<Props>(), {
  now: () => new Date(),
})

const emit = defineEmits<{
  (event: 'close'): void
  (event: 'select', result: Emit['select'])
}>()

const searchterm = ref<string>()
const selectedSearchOption = ref<'past' | 'future' | undefined>('future')

const searchPeriodPast = computed(() => {
  const startDate = subYears(props.now, 1)
  const endDate = props.now
  const label = `${formatWithLocale(startDate)} bis ${formatWithLocale(
    props.now
  )}`

  return { startDate, endDate, label }
})

const searchPeriodAll = computed(() => {
  const startDate = subYears(props.now, 1)
  const endDate = addYears(props.now, 1)
  const label = `${formatWithLocale(startDate)} bis ${formatWithLocale(
    endDate
  )}`

  return { startDate, endDate, label }
})

const searchPeriodFuture = computed(() => {
  const startDate = props.now
  const endDate = addYears(props.now, 1)
  const label = `${formatWithLocale(props.now)} bis ${formatWithLocale(
    endDate
  )}`

  return { startDate, endDate, label }
})

const searchPeriod = computed(() => {
  // Default +/- 1 year
  if (selectedSearchOption.value === 'past') {
    return searchPeriodPast.value
  }

  if (selectedSearchOption.value === 'future') {
    return searchPeriodFuture.value
  }

  return searchPeriodAll.value
})

/**
 * Load data
 */
const isSearchResultsQueryEnabled = computed(() => {
  if (!searchterm.value) return false
  if (searchterm.value.length < 3) return false

  return true
})

const searchResultsQuery = useQuery(
  SearchDocument,
  // @ts-expect-error https://github.com/vuejs/apollo/issues/1243
  () => {
    return {
      searchterm: searchterm.value,
      startDate: searchPeriod.value.startDate ?? new Date(),
      endDate: searchPeriod.value.endDate ?? new Date(),
    }
  },
  () => ({
    enabled: isSearchResultsQueryEnabled,
    // notifyOnNetworkStatusChange is needed so isLoading gets updated properly
    notifyOnNetworkStatusChange: true,
    // Don't update the cache, which might change the calendar view.
    fetchPolicy: 'no-cache',
    debounce: 500,
  })
)

const isLoading = computed(() => searchResultsQuery.loading.value)

type SearchOption = {
  id: 'past' | 'all' | 'future'
  label: string
  title: string
}

const searchOptions = computed<SearchOption[]>(() => {
  return [
    {
      id: 'past',
      label: 'Vergangenheit',
      title: searchPeriodPast.value.label,
    },
    { id: 'all', label: 'Alles', title: searchPeriodAll.value.label },
    {
      id: 'future',
      label: 'Zukunft',
      title: searchPeriodFuture.value.label,
    },
  ]
})

const normalizedSearchResults = computed(() => {
  const normalizedResults: NormalizedResult[] = []

  searchResultsQuery.result.value?.jobs?.forEach((result) => {
    if (!result) return

    const status = statusLookupJob({
      jobStateId: result.jobState?.id,
      jobTypeId: result.jobState?.jobType?.id,
    })
    const startDate = parseServerDateString(result.startDate)
    const endDate = parseServerDateString(result.endDate)

    if (!startDate) {
      throw new Error('job.startDate is undefined')
    }

    if (!endDate) {
      throw new Error('job.endDate is undefined')
    }

    if (!result.project?.id) {
      throw new Error('job.project.id is undefined')
    }

    normalizedResults.push({
      id: result.id,
      type: 'job',
      title: result.caption,
      number: result.number,
      jobStatus: status,
      startDate,
      endDate,
      project: result.project.caption,
      projectId: result.project.id,
      projectLeader: result.arrangedBy?.displayName,
      projectNumber: result.project.number,
      isCollectedWithAllJobsOfProject: result.project.setting?.isJobLayerHidden,
    })
  })

  searchResultsQuery.result.value?.stockTypeServices?.forEach((result) => {
    if (!result) return

    const startDate = parseServerDateString(result.startDate)
    const endDate = parseServerDateString(result.endDate)

    if (!startDate) {
      throw new Error('stockTypeService.startDate is undefined')
    }

    if (!endDate) {
      throw new Error('stockTypeService.endDate is undefined')
    }

    normalizedResults.push({
      id: result.id,
      type: 'stockTypeService',
      title: result.caption,
      number: result.number,
      startDate,
      endDate,
    })
  })

  searchResultsQuery.result.value?.purchaseOrders?.forEach((result) => {
    if (!result) return

    const purchaseOrderStatus = getPurchaseOrderStatus({
      id: result.purchaseOrderState?.id,
    })
    const orderedAt = parseServerDateString(result.orderedAt)

    if (!orderedAt) {
      throw new Error('purchaseOrder.orderedAt is undefined')
    }

    normalizedResults.push({
      id: result.id,
      type: 'purchaseOrder',
      title: result.caption ?? '—',
      number: result.number ?? undefined,
      purchaseOrderStatus,
      // Temporary workaround until the API provides the resource timeframe:
      // https://gitlab.dev.innovation-agents.de/ia/pro-musik/organisation/-/work_items/463
      startDate: orderedAt,
      endDate: orderedAt,
    })
  })

  return orderBy(normalizedResults, (result) => {
    return Math.abs(differenceInCalendarDays(result.startDate, props.now))
  })
})

function select(selection: SearchResultSelection) {
  const result = normalizedSearchResults.value.find((result) => {
    return result.id === selection.id && result.type === selection.type
  })

  if (!result) {
    throw new Error('Selected search result is not defined')
  }

  const type = result.isCollectedWithAllJobsOfProject ? 'project' : result.type
  const id = type === 'project' ? result.projectId : result.id

  if (!id) {
    throw new Error('search result target id is undefined')
  }

  emit('select', {
    type,
    id,
    startDate: result.startDate,
    endDate: result.endDate,
  })
}
</script>

<style lang="scss">
.PmSearch {
  $block: &;
}
</style>
